import React from 'react';
import { Row, Col, Button, Label, Input, TabContent, TabPane, CustomInput,
  Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { FaCog, FaVial, FaUsers, FaBook, FaBookOpen, FaDelicious, FaCubes } from 'react-icons/fa';
import _ from 'lodash';
import dayjs from 'dayjs';
import Slider from 'rc-slider';
import ParallelOrderArea from './ParallelOrderArea';
import ParallelCoordinates from '../Charts/ParallelCoordinates.jsx';
import StackedBarChart2 from '../Charts/StackedBarChart2';
import SettingsDialogPC from '../Dialogs/SettingsDialogPC';
import AxisDistortion from '../Core/AxisDistortion';
import MovingAverage from '../Core/MovingAverage';

const createSliderWithTooltip = Slider.createSliderWithTooltip;
const Range = createSliderWithTooltip(Slider.Range);

class OverviewPanel2 extends React.Component {
  constructor(props) {
    super();
    this.state = {
      activeSettingsIndex: -1,
      showSettingsDialog: false,
      showMessageDialog: false,
      useScaleDistortion: true,
      settingsTimeStamp: 0,
    };
    this.schema0 = [
      {name: 'author', index: 0, text: 'Author', type: 'value', message: "Author number indicates the author's ranking in the total number of EM data releases."},
      {name: 'paper', index: 1, text: 'Paper', type: 'value', message: "Papers are sorted by the number of relevant EM data."},
      {name: 'publication', index: 2, text: 'Publication', type: 'value', message: "Publication number indicates the publication's ranking in the total number of EM data releases."},
      {name: 'organism', index: 3, text: 'Organism', type: 'value', message: 'Organisms are sorted by the number of relevant EM data.'},
      {name: 'resolution', index: 4, text: 'Resolution', type: 'value', message: 'EM data resolution in angstroms.'},
      {name: 'dimension', index: 5, text: 'Dimension', type: 'value', inverse: true, message: 'The size of the volume data in the first dimension.'},
    ];
    this.schema = [
      /*{
        name: 'author', index: 0, text: 'Author',
        type: 'quadric', kernel: 'exponential', median: 0.25, min: 0, max: 15000,
        ticks: [0, 1000, 2000, 5000, 10000, 15000],
        message: "Author number indicates the author's ranking in the total number of EM data releases."
      },*/
      {
        name: 'author', index: 0, text: 'Author', icon: <FaUsers />,
        type: 'quadric', kernel: 'exp.fair', median: 0.5, min: 0, max: 650,
        ticks: [0, 100, 200, 500],
        message: "Author number indicates the author's ranking in the total number of EM data releases."
      },
      {
        name: 'publication', index: 1, text: 'Publication', icon: <FaBook />,
        type: 'quadric', kernel: 'exp.fair', median: 0.05, min: 0, max: 180,
        ticks: [0, 5, 10, 20, 40, 180],
        message: "Publication number indicates the publication's ranking in the total number of EM data releases."
      },
      {
        name: 'paper', index: 2, text: 'Paper', icon: <FaBookOpen />,
        type: 'quadric', kernel: 'exp.fair', median: 0.25, min: 0, max: 3000,
        message: "Papers are sorted by the number of relevant EM data."
      },
      {
        name: 'organism', index: 3, text: 'Organism', icon: <FaVial />,
        type: 'quadric', kernel: 'exp.fair', median: 0.05, min: 0, max: 1000,
        ticks: [0, 20, 50, 100, 1000],
        message: 'Organisms are sorted by the number of relevant EM data.'
      },
      {
        name: 'resolution', index: 4, text: 'Resolution', icon: <FaDelicious />,
        type: 'quadric', kernel: 'exp.fair', median: 0.1, min: 0, max: 150,
        ticks: [0, 3, 10, 20, 30, 150],
        message: 'EM data resolution in angstroms.'
      },
      {
        name: 'dimension', index: 5, text: 'Dimension', icon: <FaCubes />,
        type: 'quadric', kernel: 'exp.fair', median: 0.1, inverse: true, min: 0, max: 4096,
        ticks: [0, 64, 128, 256, 512, 1024, 4096],
        message: 'The size of the volume data in the first dimension.'
      },
    ];
    this.histogramSlot = 100;
    this.tabularData = [];
    this.minmax = [];
    this.histogram = [];
    this.histogramAP = [];
    this.histogramInfo = [];
    this.messageDialogReady = false;
    this.state.settings = this.schema;
    this.schema.forEach(s => this.defineMedianSettingFunc(s.index, s.name));
    this.scoreValues = this.schema.map(s => 0);
    // 测试直接采用绝对数值，而不采用排名号
    //this.valueMode = true;
  }

  componentWillReceiveProps(nextProps) {
    if(nextProps.loadTimeStamp !== this.props.filterTimeStamp)
      this.updateSummaryData(nextProps);
    this.updateVisualData(nextProps)
  }

  shouldComponentUpdate(nextProps, nextState) {
    if(nextState !== this.state)
      return true;
    if(nextProps.filterTimeStamp !== this.props.filterTimeStamp)
      return true;
    return false
  }

  toggleSettingsPanel = ((index, visible) => {
    this.props.onShowSettingPanel && this.props.onShowSettingPanel(visible);
    this.setState({activeSettingsIndex: visible ? index : -1});
  }).bind(this)

  toggleSettingsDialog = (() => {
    this.setState(prevState => ({
      showSettingsDialog: !prevState.showSettingsDialog,
    }));
  }).bind(this)

  applySettings = (settings => {
    settings.forEach((s, index) => {
      settings[index] = this.schema[s.index];
    })
    this.setState({settings, settingsTimeStamp: new Date().getTime()})
  }).bind(this)

  toggleMessageDialog = (() => {
    this.setState(prevState => ({
      showMessageDialog: !prevState.showMessageDialog,
    }));
  }).bind(this)

  showMessage = (() => {
    this.messageDialogReady = false;
    this.setState({showMessageDialog: true});
  }).bind(this)

  toggleScaleDistortion = (() => {
    this.setState(prevState => ({
      useScaleDistortion: !prevState.useScaleDistortion,
    }));
  }).bind(this)

  defineMedianSettingFunc = (index, name) => {
    this.state[name + 'Median'] = Math.round(this.schema[index].median * 100);
    this[name + 'SetMedian'] = (val => {
      let {settings} = this.state;
      let sidx = settings.findIndex(s => s.name === name);
      settings[sidx].median = val * 0.01;
      this.updateHistogramAP(index, settings[sidx]);
      this.setState({[`${name}Median`]: val, settings});
    }).bind(this);
  }

  updateSummaryData(props) {
    if(!props.base)
      return;
    let { levels } = props.base;

    // summary update
    let levelCount = levels[0].length;
    let levelMidPos = Math.floor((levels[0].length - 1) / 2);
    this.schema.forEach((s, index) => {
      let levelsI = levels[index];
      this.minmax[index] = [Math.min(levelsI[0], levelsI[levelCount-1]),
        Math.max(levelsI[0], levelsI[levelCount-1])];
      //console.log(`minmax(${index}) : ${this.minmax[index][0]} - ${this.minmax[index][1]}`);
      s.max = this.minmax[index][1];
      s.median = levelsI[levelMidPos] / s.max;
      let t1 = Math.floor(levelMidPos / 2);
      let t3 = Math.floor(levelMidPos + levelMidPos / 2);
      s.ticks = [levelsI[0], levelsI[t1], levelsI[levelMidPos], levelsI[t3], levelsI[levelCount-1]];
    })

    // convert all to value mode
    if(this.valueMode) {
      let {
        listOrganism,
        listTeam,
        listPapers,
        listPublication,
      } = props.base;
      this.schema[0].max = listTeam[0].work.length;
      this.schema[0].median = listTeam[levels[0][levelMidPos]].work.length / this.schema[0].max;
      this.schema[0].inverse = true;
      this.schema[1].max = listPublication[0].count;
      this.schema[1].median = listPublication[levels[1][levelMidPos]].count / this.schema[1].max;
      this.schema[1].inverse = true;
      this.schema[2].max = listPapers[0].count;
      this.schema[2].median = listPapers[levels[2][levelMidPos]].count / this.schema[2].max;
      this.schema[2].inverse = true;
      this.schema[3].max = listOrganism[0].count;
      this.schema[3].median = listOrganism[levels[3][levelMidPos]].count / this.schema[3].max;
      this.schema[3].inverse = true;
    }
  }

  updateVisualData(props) {
    if(!props.base || !props.filtered)
      return;
    let {
      listEMMethod,
      listOrganism,
      listAuthorFull,
      listTeam,
      listPapers,
      listPublication,
      dataListEMDs,
      levels,
    } = props.base;
    let {
      filterResults,
      filterMap,
    } = props.filtered;

    this.filterResultsByEMMethod = listEMMethod.map(m => []);
    filterResults.forEach(i => {
      this.filterResultsByEMMethod[dataListEMDs[i].m].push(i);
    });
    this.tabularData = this.filterResultsByEMMethod.map((m, index) => ({
      name: listEMMethod[index].name,
      nameShort: listEMMethod[index].short,
      data: m.map(i => [
        dataListEMDs[i].u[0],
        dataListEMDs[i].p,
        dataListEMDs[i].d,
        dataListEMDs[i].o[0],
        dataListEMDs[i].r || 0,
        dataListEMDs[i].g[0],
        dataListEMDs[i].r !== null,
      ]),
    }));
    // level score
    this.scoreValues = [];
    let allValues, meanValue, meanRank;
    // level score: author
    allValues = this.tabularData.map(t => t.data.map(m => (m[0] !== undefined) ? listTeam[m[0]].work.length : 0)).reduce((a,b) => a.concat(b)).filter(r => r);
    if(allValues.length > 0) {
      meanValue = allValues.reduce((a,b) => a+b) / allValues.length;
      meanRank = listTeam.findIndex(f => f.work.length <= meanValue);
      this.scoreValues.push(10 - levels[0].findIndex(v => v >= meanRank));
    }
    else this.scoreValues.push(0);
    // level score: publication
    allValues = this.tabularData.map(t => t.data.map(m => (m[1] !== undefined && m[1] !== null) ? listPublication[m[1]].count : 0)).reduce((a,b) => a.concat(b)).filter(r => r);
    if(allValues.length > 0) {
      meanValue = allValues.reduce((a,b) => a+b) / allValues.length;
      meanRank = listPublication.findIndex(f => f.count <= meanValue);
      this.scoreValues.push(10 - levels[1].findIndex(v => v >= meanRank));
    }
    else this.scoreValues.push(0);
    // level score: paper
    allValues = this.tabularData.map(t => t.data.map(m => (m[2] !== undefined && m[2] !== null) ? listPapers[m[2]].count : 0)).reduce((a,b) => a.concat(b)).filter(r => r);
    if(allValues.length > 0) {
      meanValue = allValues.reduce((a,b) => a+b) / allValues.length;
      meanRank = listPapers.findIndex(f => f.count <= meanValue);
      this.scoreValues.push(10 - levels[2].findIndex(v => v >= meanRank));
    }
    else this.scoreValues.push(0);
    // level score: organism
    allValues = this.tabularData.map(t => t.data.map(m => (m[3] !== undefined) ? listOrganism[m[3]].count : 0)).reduce((a,b) => a.concat(b)).filter(r => r);
    if(allValues.length > 0) {
      meanValue = allValues.reduce((a,b) => a+b) / allValues.length;
      meanRank = listOrganism.findIndex(f => f.count <= meanValue);
      this.scoreValues.push(10 - levels[3].findIndex(v => v >= meanRank));
    }
    else this.scoreValues.push(0);
    // level score: resolution
    allValues = this.tabularData.map(t => t.data.map(m => (m[4] !== undefined) ? +m[4] : 0)).reduce((a,b) => a.concat(b)).filter(r => r);
    if(allValues.length > 0) {
      meanValue = allValues.reduce((a,b) => a+b) / allValues.length;
      this.scoreValues.push(10 - levels[4].findIndex(v => v >= meanValue));
    }
    else this.scoreValues.push(0);
    // level score: grid number
    allValues = this.tabularData.map(t => t.data.map(m => (m[5] !== undefined) ? +m[5] : 0)).reduce((a,b) => a.concat(b)).filter(r => r);
    if(allValues.length > 0) {
      meanValue = allValues.reduce((a,b) => a+b) / allValues.length;
      this.scoreValues.push(10 - levels[5].findIndex(v => v <= meanValue));
    }
    else this.scoreValues.push(0);
    // overview background
    this.tabularData.push({
      name: 'All',
      nameShort: 'All',
      data: dataListEMDs.map(m => [m.u[0], m.p, m.d, m.o[0], m.r || 0, m.g[0], m.r !== null]),
    })
    // PCP contours
    this.tabularData.push({
      name: 'Contour',
      nameShort: 'Rank Contour',
      data: Array.from({length: levels.length ? levels[0].length : 0},
        (c, i) => [levels[0][i], levels[1][i], levels[2][i], levels[3][i], levels[4][i], levels[5][i], true]),
    })
    // summary update
    this.schema.forEach((s, index) => {
      this.updateHistogram(index);
      let sidx = this.state.settings.findIndex(s1 => s1.name === s.name);
      this.updateHistogramAP(index, this.state.settings[sidx]);
    })
    // for unknown resolutions
    this.tabularData.forEach(method => method.data.forEach(m => m[4] = m[6] ? m[4] : this.minmax[4][1]))

    // convert all to value mode
    if(this.valueMode) {
      this.tabularData.forEach(t => t.data.forEach(m => {
        m[0] = (m[0] !== undefined) ? listTeam[m[0]].work.length : 0;
        m[1] = (m[1] !== undefined && m[1] !== null) ? listPublication[m[1]].count : 0;
        m[2] = (m[2] !== undefined && m[2] !== null) ? listPapers[m[2]].count : 0;
        m[3] = (m[3] !== undefined) ? listOrganism[m[3]].count : 0;
      }))
    }
  }

  updateHistogram(index) {
    let elems = _.reduce(this.tabularData.map(m => m.data.map(d => d[index])), (flattened, other) => flattened.concat(other));
    let vMin = this.minmax[index][0], vMax = this.minmax[index][1];
    let segLen = (vMax - vMin) / (this.histogramSlot + 0.99);
    let factor = 1. / segLen;
    let sumObject = _.countBy(elems, e => Math.floor((e - vMin) * factor));
    let sumResults = [];
    Object.keys(sumObject).forEach(key => (!isNaN(key)) && (sumResults[key] = sumObject[key]));
    sumResults = MovingAverage.ma(sumResults, 5);
    this.histogram[index] = sumResults && sumResults.map((a,i) => ({
      name: `${vMin + i * segLen} ~ ${vMin + i * segLen + segLen}`,
      id: i,
      values: [a]
    }))
  }

  updateHistogramAP(index, settings) {
    let elems = _.reduce(this.tabularData.map(m => m.data.map(d => AxisDistortion.apply(settings, this.minmax[index], d[index]))), (flattened, other) => flattened.concat(other));
    let factor = this.histogramSlot + 0.99;
    let segLen = 1. / factor;
    let sumObject = _.countBy(elems, e => Math.floor(e * factor));
    let sumResults = [];
    Object.keys(sumObject).forEach(key => (!isNaN(key)) && (sumResults[key] = sumObject[key]));
    sumResults = MovingAverage.ma(sumResults, 5);
    this.histogramAP[index] = sumResults && sumResults.map((a,i) => ({
      name: `${i * segLen} ~ ${i * segLen + segLen}`,
      id: i,
      values: [a]
    }))
  }

  render() {
    let {sizeFactor} = this.props;
    if(!sizeFactor) sizeFactor = 1;
    let {activeSettingsIndex, settings, useScaleDistortion} = this.state;
    let pcpWidth = this.props.flat ? activeSettingsIndex >= 0 ? 900 : 1200 : activeSettingsIndex >= 0 ? 450 : 600;
    if(!this.messageDialogReady && this.state.showMessageDialog) {
      this.messageDialogReady = true;
      setTimeout(1000, this.forceUpdate.bind(this)); // hack for render delay problem
    }
    return (
      <div>
        <Row>
          <Col sm={activeSettingsIndex >= 0 ? 9 : 12}>
            <Row>
              <Col>
                <ParallelOrderArea
                  width={pcpWidth * sizeFactor}
                  values={this.scoreValues}
                  settings={settings}
                  active={activeSettingsIndex >= 0 ? this.schema[activeSettingsIndex].name : ''}
                  onChange={this.applySettings}
                  onToggle={this.toggleSettingsPanel}
                  sizeFactor={sizeFactor}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <ParallelCoordinates
                  width={pcpWidth * sizeFactor}
                  height={400 * sizeFactor}
                  schema={settings}
                  distortScale={useScaleDistortion}
                  data={this.tabularData}
                  sizeFactor={sizeFactor}
                />
              </Col>
            </Row>
          </Col>
          {activeSettingsIndex >= 0 ? <Col className='settings-panel'>
            <TabContent className='settings-content' activeTab={this.schema[activeSettingsIndex].name}>
              {this.schema.map((s, index) => {
                let nameU = `${s.name[0].toUpperCase()}${s.name.slice(1)}`;
                return <TabPane tabId={s.name}>
                  {this.props.base.overviewEMDs.debug && <div>
                    <CustomInput type="switch" label="Scale Distortion" id={s.name + "-switch"}
                      checked={useScaleDistortion}
                      onChange={this.toggleScaleDistortion}
                    />
                    <Label for="distort-type">Distortion type</Label>
                    <Input type="select" name="select" size="sm" id={s.name + "-distort-type"}
                      value={s.kernel === 'exp.fair' ? 'Fair' : 'Even'}
                      onChange={e => {
                        s.kernel = e.target.value === 'Fair' ? 'exp.fair' : 'exp.even';
                        this.applySettings(settings);
                      }}
                    >
                      <option>Fair</option>
                      <option>Even</option>
                    </Input><br />
                  </div>}
                  <Label for="">Median position</Label>
                  <Slider id="median" min={1} max={50}
                    value={this.state[`${s.name}Median`]}
                    onChange={this[`${s.name}SetMedian`]}
                  />
                  <br /><Button color='danger' size='sm'
                    onClick={() => this.showMessage()}
                  >Column Info ({nameU})</Button>
                </TabPane>
              })}
            </TabContent>
          </Col> : <div />}
        </Row>
        <Button
          className="settings"
          style={{ display: 'none', position: 'absolute', top: '30px', right: '15px' }}
          onClick={this.toggleSettingsDialog}
        ><FaCog /></Button>
        <SettingsDialogPC
          isOpen={this.state.showSettingsDialog}
          toggle={this.toggleSettingsDialog}
          schema={this.schema}
          settings={settings}
          onApply={this.applySettings}
        />
        <Modal isOpen={this.state.showMessageDialog} toggle={this.toggleMessageDialog}
          style={this.props.base.overviewEMDs.debug ? { 'max-width': '800px', margin: 'auto' } : {}}
        >
          {activeSettingsIndex >= 0 && <ModalBody>
            {this.schema[activeSettingsIndex].message}
            {this.props.base.overviewEMDs.debug && <div>
              <StackedBarChart2
                noanim={true}
                width={600}
                height={300}
                listRange={[0, this.histogramSlot]}
                schema={[{name: 'histogram', id: 'histogram'}]}
                data={this.histogram[activeSettingsIndex]}
              />
              <StackedBarChart2
                noanim={true}
                width={600}
                height={300}
                listRange={[0, this.histogramSlot]}
                schema={[{name: 'histogram', id: 'histogram'}]}
                data={this.histogramAP[activeSettingsIndex]}
              />
              <Col className='settings-panel'>
                <Label for="distort-type">Distortion type</Label>
                <Input type="select" name="select" size="sm" id="distort-type"
                  value={this.schema[activeSettingsIndex].kernel === 'exp.fair' ? 'Fair' : 'Even'}
                  onChange={e => {
                    this.schema[activeSettingsIndex].kernel = e.target.value === 'Fair' ? 'exp.fair' : 'exp.even';
                    this.applySettings(settings);
                  }}
                >
                  <option>Fair</option>
                  <option>Even</option>
                </Input><br />
                <Label for="">Median position</Label>
                <Slider id="median" min={1} max={50}
                  value={this.state[`${this.schema[activeSettingsIndex].name}Median`]}
                  onChange={this[`${this.schema[activeSettingsIndex].name}SetMedian`]}
                />
              </Col>
            </div>}
          </ModalBody>}
          <ModalFooter>
            <Button color='secondary' onClick={this.toggleMessageDialog}>Close</Button>
          </ModalFooter>
        </Modal>
      </div>
    )
  }
}

export default OverviewPanel2;
